import {
	syncDiscountCartAttributeFromCart,
	updateCartAttributes,
} from '../core/cart-attribution'
import { whenElementReady } from '../utils/helpers'
import { applyDiscountCookie } from './discount-cookie'

const STORAGE_KEY = 'pv:discount_code'
const VERIFY_ATTEMPTS = 4
const VERIFY_WAIT_MS = 200

type DiscountSourceDetail = 'url_param' | 'stored_retry' | 'add_to_cart'

type DiscountApplication = {
	type?: string
	title?: string
}

type LineLevelDiscountAllocation = {
	discount_application?: {
		type?: string
		title?: string
	}
}

type DiscountAwareCart = {
	cart_level_discount_applications?: DiscountApplication[]
	items?: Array<{
		line_level_discount_allocations?: LineLevelDiscountAllocation[]
	}>
}

let domRetryRegistered = false
let applyAttemptInFlight: Promise<boolean> | null = null

function trackDiscountEvent(
	eventName: string,
	code: string,
	sourceDetail: DiscountSourceDetail,
	extra: Record<string, unknown> = {}
) {
	const payload = {
		discount_code: code,
		source_detail: sourceDetail,
		sent_from: 'Pvolve Web Script',
		...extra,
	}

	if (window.SegmentUser?.sendSegmentEvent) {
		void window.SegmentUser.sendSegmentEvent(null, {
			eventName,
			...payload,
		})
		return
	}

	window.analytics?.track?.(eventName, payload)
}

function trackUrlDiscountCaptured(code: string) {
	trackDiscountEvent('URL Discount Captured', code, 'url_param')
}

function normalizeDiscountCode(
	code: string | undefined | null
): string | undefined {
	const trimmed = code?.trim()
	if (!trimmed) return undefined
	const firstCode = trimmed.split(',')[0]?.trim()
	return firstCode || undefined
}

function getStoredDiscountCode(): string | undefined {
	try {
		return normalizeDiscountCode(window.localStorage.getItem(STORAGE_KEY))
	} catch (_error) {
		return undefined
	}
}

export function setStoredDiscountCode(code: string | null | undefined) {
	try {
		const normalized = normalizeDiscountCode(code)
		if (!normalized) {
			window.localStorage.removeItem(STORAGE_KEY)
			return
		}
		window.localStorage.setItem(STORAGE_KEY, normalized)
	} catch (_error) {
		// noop: storage can be unavailable in some browser modes
	}
}

export function getStoredDiscountCodeForUi() {
	return getStoredDiscountCode()
}

async function consumeUrlDiscountCode() {
	const url = new URL(window.location.href)
	const discountKeys = Array.from(url.searchParams.keys()).filter(
		(key) => key.toLowerCase() === 'discount'
	)

	let urlCode: string | undefined
	for (const key of discountKeys) {
		if (!urlCode) {
			urlCode = normalizeDiscountCode(url.searchParams.get(key))
		}
		url.searchParams.delete(key)
	}

	const redirectPath = `${url.pathname}${url.search}${url.hash}`

	if (discountKeys.length) {
		window.history.replaceState({}, '', redirectPath)
	}

	if (urlCode && url.pathname.endsWith('/sf-activation')) {
		const formEl = document
			.querySelector<HTMLElement>('[data-activation-form]')
			?.querySelector<HTMLFormElement>('form')
		
		if (typeof formEl !== 'undefined' && formEl !== null) {
			const inputEl = formEl.querySelector('input[name="code"]') as HTMLInputElement
			inputEl.value = urlCode
			const readyForm = await whenElementReady(formEl, 'initialized', 'true')
			readyForm.requestSubmit()
			
		}
	}

	return {
		code: urlCode,
		hadDiscountParam: discountKeys.length > 0,
	}
}

function normalizeCompareCode(code: string | undefined | null) {
	return (code || '').trim().toLowerCase()
}

function codesEqual(
	a: string | undefined | null,
	b: string | undefined | null
) {
	const left = normalizeCompareCode(a)
	const right = normalizeCompareCode(b)
	if (!left || !right) return false
	return left === right
}

function unique(values: string[]) {
	return Array.from(new Set(values.filter(Boolean)))
}

function extractDiscountCodesFromCart(cart: DiscountAwareCart): string[] {
	const cartLevel = (cart.cart_level_discount_applications || [])
		.filter(
			(discount) => discount.type === 'discount_code' && discount.title
		)
		.map((discount) => (discount.title || '').trim())

	const lineLevel = (cart.items || []).flatMap((item) => {
		return (item.line_level_discount_allocations || [])
			.map((allocation) => allocation.discount_application)
			.filter(
				(application) =>
					application?.type === 'discount_code' && application.title
			)
			.map((application) => (application?.title || '').trim())
	})

	return unique([...cartLevel, ...lineLevel])
}

function isDiscountAlreadyApplied(code: string) {
	const activeCodes = window.cartDiscounts || []
	return activeCodes.some((activeCode) => codesEqual(activeCode, code))
}

async function fetchDiscountAwareCart(options?: {
	forceRefresh?: boolean
}): Promise<DiscountAwareCart> {
	if (options?.forceRefresh) {
		return fetch('/cart/update.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify({ updates: {} }),
		}).then((response) => response.json() as Promise<DiscountAwareCart>)
	}

	return fetch('/cart.js', {
		method: 'GET',
		cache: 'no-store',
		headers: {
			'Content-Type': 'application/json',
		},
	}).then((response) => response.json() as Promise<DiscountAwareCart>)
}

async function updateCartDiscount(
	code: string
): Promise<DiscountAwareCart | null> {
	const response = await fetch('/cart/update.js', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({ discount: code }),
	})

	if (!response.ok) return null

	return response.json() as Promise<DiscountAwareCart>
}

function wait(ms: number) {
	return new Promise<void>((resolve) => {
		window.setTimeout(resolve, ms)
	})
}

function isCodeActiveInCart(cart: DiscountAwareCart, code: string) {
	const codes = extractDiscountCodesFromCart(cart)
	return codes.some((activeCode) => codesEqual(activeCode, code))
}

function syncWindowCartDiscounts(cart: DiscountAwareCart) {
	window.cartDiscounts = extractDiscountCodesFromCart(cart)
}

async function syncDiscountAttribution(code: string, cart?: DiscountAwareCart) {
	if (cart) {
		syncWindowCartDiscounts(cart)
	}
	await syncDiscountCartAttributeFromCart(cart).catch(() => {
		void updateCartAttributes({ discount: code })
	})
}

function dispatchCartChanged() {
	document.documentElement.dispatchEvent(new CustomEvent('cart:changed'))
}

function ensureDomReadyRetryRegistered() {
	if (domRetryRegistered) return
	domRetryRegistered = true

	const runRetry = () => {
		void ensureDiscountAppliedFromStorage('stored_retry')
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', runRetry, { once: true })
		return
	}

	runRetry()
}

async function applyAndVerifyDiscount(
	code: string,
	sourceDetail: DiscountSourceDetail
) {
	trackDiscountEvent('Auto Discount Apply Attempted', code, sourceDetail)

	let firstCart = await updateCartDiscount(code)
	if (!firstCart) {
		const applied = await applyDiscountCookie(code)
		if (!applied) {
			trackDiscountEvent(
				'Auto Discount Apply Failed',
				code,
				sourceDetail,
				{
					reason: 'apply_request_failed',
				}
			)
			return false
		}
	}

	let lastCart: DiscountAwareCart | undefined = firstCart || undefined
	for (let attempt = 0; attempt < VERIFY_ATTEMPTS; attempt += 1) {
		const cart =
			attempt === 0 && firstCart
				? firstCart
				: await fetchDiscountAwareCart({ forceRefresh: attempt === 0 })
		lastCart = cart
		firstCart = null
		if (isCodeActiveInCart(cart, code)) {
			await syncDiscountAttribution(code, cart)
			dispatchCartChanged()
			trackDiscountEvent(
				'Auto Discount Apply Succeeded',
				code,
				sourceDetail,
				{
					attempt: attempt + 1,
				}
			)
			return true
		}

		if (attempt < VERIFY_ATTEMPTS - 1) {
			await wait(VERIFY_WAIT_MS)
		}
	}

	if (lastCart) {
		syncWindowCartDiscounts(lastCart)
	}

	trackDiscountEvent('Auto Discount Apply Failed', code, sourceDetail, {
		reason: 'discount_not_active_after_apply',
		attempts: VERIFY_ATTEMPTS,
	})
	return false
}

export async function bootstrapDiscountCode() {
	const { code: urlCode, hadDiscountParam } = await consumeUrlDiscountCode()
	const storedCode = getStoredDiscountCode()
	const themeCode = normalizeDiscountCode(window.themeSettings?.discountCode)

	const selectedCode = urlCode || storedCode || themeCode
	if (!selectedCode) {
		ensureDomReadyRetryRegistered()
		return
	}

	if (urlCode) {
		trackUrlDiscountCaptured(urlCode)
	}

	setStoredDiscountCode(selectedCode)
	ensureDomReadyRetryRegistered()

	if (!urlCode && isDiscountAlreadyApplied(selectedCode)) {
		await syncDiscountAttribution(selectedCode)
		return
	}

	await applyAndVerifyDiscount(
		selectedCode,
		hadDiscountParam ? 'url_param' : 'stored_retry'
	)
}

export async function ensureDiscountAppliedFromStorage(
	sourceDetail: DiscountSourceDetail = 'stored_retry'
) {
	if (applyAttemptInFlight) {
		return applyAttemptInFlight
	}

	applyAttemptInFlight = (async () => {
		const storedCode = getStoredDiscountCode()
		if (!storedCode) return false

		const cart = await fetchDiscountAwareCart()
		if (isCodeActiveInCart(cart, storedCode)) {
			await syncDiscountAttribution(storedCode, cart)
			return true
		}

		return applyAndVerifyDiscount(storedCode, sourceDetail)
	})()

	try {
		return await applyAttemptInFlight
	} finally {
		applyAttemptInFlight = null
	}
}
